import { createAsyncThunk } from '@reduxjs/toolkit';

import instance from 'configs/requests';
import { TicketStatus } from 'constants/enums';
import { logoutWithError } from 'store/oauth/reducer';

export const fetchGroups = createAsyncThunk(
  '/groups/getBasicGroups',
  async (arg: { isSysop?: boolean } | void, { dispatch, rejectWithValue }) => {
    const { data } = await instance.get('/clinician-gateway/group');
    if (!arg?.isSysop && (data as IGroupInfo[]).length === 0) {
      dispatch(logoutWithError('noAccess'));
      return rejectWithValue('noAccess');
    }
    return data as IGroupInfo[];
  }
);

export const fetchTagSVGs = createAsyncThunk('groups/getTagSVGs', async () => {
  const { data } = await instance.get('/clinician-gateway/group/tags/svgs');
  return data as ITagSvg[];
});

export const fetchTagLabels = createAsyncThunk('groups/getTagLabels', async () => {
  const { data } = await instance.get('/clinician-gateway/group/tags/labels');
  return data as IGroupTagLabels[];
});

export const fetchGroupAdmins = createAsyncThunk('groups/getGroupAdmins', async () => {
  const { data } = await instance.get('/clinician-gateway/group/admins');
  return data as IAdmin[];
});

export const fetchTimeLogTasks = createAsyncThunk('groups/getTimeLogTasks', async () => {
  const { data } = await instance.get('/clinician-gateway/time-logs/tasks');
  return data as ILogTask[];
});

export const fetchTimeLogTypes = createAsyncThunk('groups/getTimeLogTypes', async () => {
  const { data } = await instance.get('/clinician-gateway/time-logs/types');
  return data as ILogType[];
});

export const fetchGroupAvatar = createAsyncThunk('groups/getGroupAvatar', async (gid: string) => {
  const { data } = await instance.get(`/clinician-gateway/group/${gid}/avatar`);
  return data as IGroupAvatar;
});

export const fetchGroupCarePrograms = createAsyncThunk('groups/getCarePrograms', async () => {
  const { data } = await instance.get('/clinician-gateway/group/programs');
  return data as IGroupCarePrograms[];
});

export const fetchObservationCollections = createAsyncThunk(
  'groups/getObservationCollections',
  async () => {
    const { data } = await instance.get('/clinician-gateway/observation-collections');
    return data as IObservationCollection[];
  }
);

export const fetchDeviceOrderings = createAsyncThunk(
  'groups/getDeviceOrderings',
  async (gid: string) => {
    const { data } = await instance.get(`/clinician-gateway/groups/${gid}/device-orderings`);
    return data as IDeviceOrderingCatalogItem[];
  }
);

export const getLastBulkShippingInfo = async (gid: string) => {
  const { data } = await instance.get<IDeviceOrderLastBulkShippingResponse>(
    '/clinician-gateway/device/order/bulk/last-shipping-info',
    { params: { gid } }
  );
  return data.shippingInfo;
};

export const fetchNotifyActivities = createAsyncThunk(
  'groups/getNotifyActivities',
  async (gid: string) => {
    const { data } = await instance.get(`clinician-gateway/group/${gid}/notify-activities`);
    return data as INotifyActivity[];
  }
);

export const updateNotifyActivity = createAsyncThunk(
  'groups/updateNotifyActivity',
  async ({ gid, notifyActivities }: { gid: string; notifyActivities: IGroupNotificationForm }) => {
    const { notifyEmail, activities } = notifyActivities;
    const { data } = await instance.post(`/clinician/Group/${gid}/notify-activity`, {
      notifyEmail,
      activities
    });
    return data.map((activity: any) => {
      return {
        activityType: parseInt(activity.activity_type) as 0 | 1,
        deleted: parseInt(activity.deleted) as 0 | 1,
        email: activity.email,
        gid: activity.gid,
        serverModifiedDate: parseInt(activity.server_modified_date),
        modifiedBy: activity.modified_by
      };
    });
  }
);

export const getAlertsCount = createAsyncThunk('groups/getAlertsCount', async (gid: string) => {
  const { data } = await instance.get('/clinician/Alert/Count', {
    params: {
      status: 0,
      groupByUid: 0,
      specificGid: gid !== 'all' ? gid : null
    }
  });
  return data as any;
});

export const getOpenTicketsCount = createAsyncThunk(
  'groups/getOpenTicketsCount',
  async ({ gid }: { gid: string }) => {
    if (gid === 'all') {
      return 0;
    }

    const { data } = await instance.get('/clinician-gateway/support/tickets', {
      params: { gid }
    });

    return data.filter(
      (ticket: any) => ticket.status === TicketStatus.OPEN || ticket.status === TicketStatus.PENDING
    ).length;
  }
);

export const fetchQuestionnaires = createAsyncThunk(
  'groups/fetchQuestionnaires',
  async ({ gid, scope }: { gid: string; scope: string }) => {
    const { data } = await instance.get('/clinician-gateway/questionnaires', {
      params: { gid, scope }
    });
    return data as ISurvey[];
  }
);
